import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Descriptions, Empty, Form, Input, InputNumber, Modal, Select, Spin, Statistic, Table, Tag, Typography, message } from 'antd'
import { CopyOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { createRuntimeAsset, getRuntimeAssetDashboardSummary, getRuntimeAssetDetail } from '../../request'
import { envHttpUrl } from '../../http/globalMethod'
import { display, formatDateTime } from './aicDashboardUtils'

const assetTypeOptions = [
  { value: 'vibe_project', label: 'Vibe 项目' },
  { value: 'microservice', label: '微服务' },
  { value: 'collector', label: '采集器' },
  { value: 'scheduled_task', label: '周期任务' },
  { value: 'external_system', label: '外部系统' }
]

const assetTypeLabels = assetTypeOptions.reduce((result, item) => ({ ...result, [item.value]: item.label }), {})
const statusMeta = {
  healthy: { badge: 'success', label: '健康' },
  warning: { badge: 'warning', label: '预警' },
  failure: { badge: 'error', label: '故障' },
  unknown: { badge: 'default', label: '未知' }
}
const visibilityLabels = { private: '仅自己可见', department: '部门可见', company: '全员可见' }
const eventTypeLabels = {
  HEARTBEAT: '心跳', RUN_STARTED: '运行开始', RUN_SUCCEEDED: '运行成功', RUN_FAILED: '运行失败',
  DEPLOY_STARTED: '部署开始', DEPLOY_SUCCEEDED: '部署成功', DEPLOY_FAILED: '部署失败',
  CHECK_SUCCEEDED: '检查成功', CHECK_FAILED: '检查失败'
}

const getStatusMeta = status => statusMeta[status] || statusMeta.unknown

const intervalText = minutes => {
  if (!minutes && minutes !== 0) return '-'
  if (minutes % 1440 === 0) return `${minutes / 1440} 天`
  if (minutes % 60 === 0) return `${minutes / 60} 小时`
  return `${minutes} 分钟`
}

const padTime = value => String(value).padStart(2, '0')

const formatRuntimeEventObservedAt = date => {
  const year = date.getFullYear()
  const month = padTime(date.getMonth() + 1)
  const day = padTime(date.getDate())
  const hour = padTime(date.getHours())
  const minute = padTime(date.getMinutes())
  const second = padTime(date.getSeconds())
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`
}

const getRuntimeReportEndpoint = () => {
  const baseUrl = (envHttpUrl() || {}).baseUrl || '//scmapi.lechun.cc'
  const normalizedBaseUrl = baseUrl.indexOf('//') === 0 ? `https:${baseUrl}` : baseUrl
  return `${normalizedBaseUrl}/lechun-baseservice/reliability/events`
}

function RuntimeAssetDetail ({ assetId, visible, onClose }) {
  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!visible || !assetId) return undefined
    let mounted = true
    setLoading(true)
    setError('')
    getRuntimeAssetDetail(assetId)
      .then(value => { if (mounted) setDetail(value) })
      .catch(() => { if (mounted) setError('运行资产详情加载失败') })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [assetId, visible])

  const meta = getStatusMeta(detail && detail.healthStatus)
  const events = detail && Array.isArray(detail.recentEvents) ? detail.recentEvents : []
  return <Modal visible={visible} width={920} footer={null} title="运行资产详情" onCancel={onClose} destroyOnClose>
    <Spin spinning={loading}>
      {error && <Alert showIcon type="error" message={error} />}
      {detail ? <div className="aic-runtime-detail">
        <div className="aic-runtime-detail-title">
          <div><Typography.Title level={3}>{display(detail.name)}</Typography.Title><span>{display(assetTypeLabels[detail.assetType])} · {display(detail.systemName)} · {display(detail.environment)}</span></div>
          <Badge status={meta.badge} text={meta.label} />
        </div>
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="资产标识" span={2}>{display(detail.assetKey)}</Descriptions.Item>
          <Descriptions.Item label="负责人">{display(detail.ownerName)}</Descriptions.Item>
          <Descriptions.Item label="部门">{display(detail.ownerDepartmentName)}</Descriptions.Item>
          <Descriptions.Item label="可见范围">{display(visibilityLabels[detail.visibilityScope])}</Descriptions.Item>
          <Descriptions.Item label="时区">{display(detail.timezone)}</Descriptions.Item>
          <Descriptions.Item label="预期周期">{intervalText(detail.expectedIntervalMinutes)}</Descriptions.Item>
          <Descriptions.Item label="宽限时间">{intervalText(detail.gracePeriodMinutes)}</Descriptions.Item>
          <Descriptions.Item label="最近成功">{formatDateTime(detail.lastSuccessAt, '-')}</Descriptions.Item>
          <Descriptions.Item label="下次期望">{formatDateTime(detail.nextExpectedAt, '-')}</Descriptions.Item>
          <Descriptions.Item label="判定依据" span={2}>{display(detail.healthReason)}</Descriptions.Item>
          <Descriptions.Item label="说明" span={2}>{display(detail.description)}</Descriptions.Item>
        </Descriptions>
        <Card size="small" className="aic-runtime-events" title={`最近运行记录（${events.length}）`}>
          <Table
            size="small"
            pagination={false}
            rowKey={row => row.eventId}
            dataSource={events}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚未收到运行上报" /> }}
            columns={[
              { title: '事件', dataIndex: 'eventType', render: value => display(eventTypeLabels[value] || value) },
              { title: '结果', dataIndex: 'result', render: value => <Tag color={value === 'success' ? 'green' : value === 'failed' ? 'red' : 'blue'}>{display(value)}</Tag> },
              { title: '观测时间', dataIndex: 'observedAt', render: value => formatDateTime(value, '-') },
              { title: '耗时', dataIndex: 'durationMs', render: value => value === null || value === undefined ? '-' : `${value} ms` },
              { title: '数据量', dataIndex: 'recordCount', render: value => value === null || value === undefined ? '-' : value },
              { title: '来源', dataIndex: 'source', render: display },
              { title: '错误摘要', dataIndex: 'errorSummary', render: display }
            ]}
          />
        </Card>
      </div> : !loading && !error && <Empty description="暂无运行资产详情" />}
    </Spin>
  </Modal>
}

function CredentialModal ({ registration, onClose }) {
  if (!registration) return null
  const command = [
    `curl -X POST '${getRuntimeReportEndpoint()}'`,
    "  -H 'Content-Type: application/json'",
    `  -H 'X-Asset-Key: ${registration.reportingKey}'`,
    `  -d '{"eventId":"replace-with-uuid","eventType":"RUN_SUCCEEDED","observedAt":"${formatRuntimeEventObservedAt(new Date())}","source":"self_report"}'`
  ].join(' \\\n')
  const copy = value => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return message.warning('当前浏览器不支持自动复制，请手动复制')
    navigator.clipboard.writeText(value).then(() => message.success('已复制')).catch(() => message.warning('复制失败，请手动复制'))
  }
  return <Modal visible width={760} title="运行资产登记成功" footer={<Button type="primary" onClick={onClose}>我已安全保存</Button>} closable={false} maskClosable={false}>
    <Alert
      showIcon
      type="warning"
      message="上报密钥仅展示这一次"
      description="请立即保存到上报程序的环境变量或密钥管理服务中。看板不会再次显示明文密钥。"
    />
    <div className="aic-runtime-secret-row"><span>资产标识</span><code>{registration.assetKey}</code><Button icon={<CopyOutlined />} onClick={() => copy(registration.assetKey)}>复制</Button></div>
    <div className="aic-runtime-secret-row"><span>上报密钥</span><code>{registration.reportingKey}</code><Button icon={<CopyOutlined />} onClick={() => copy(registration.reportingKey)}>复制</Button></div>
    <Typography.Title level={5}>首次上报示例</Typography.Title>
    <pre className="aic-runtime-command">{command}</pre>
    <Button icon={<CopyOutlined />} onClick={() => copy(command)}>复制 curl</Button>
  </Modal>
}

export default function RuntimeAssets ({ Nav }) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [summary, setSummary] = useState({ counts: {}, assets: [] })
  const [keyword, setKeyword] = useState('')
  const [type, setType] = useState('all')
  const [status, setStatus] = useState('all')
  const [registerVisible, setRegisterVisible] = useState(false)
  const [registration, setRegistration] = useState(null)
  const [detailId, setDetailId] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    setError('')
    return getRuntimeAssetDashboardSummary({})
      .then(value => setSummary(value || { counts: {}, assets: [] }))
      .catch(() => setError('运行资产加载失败，请确认数据库脚本已执行且后端已部署'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const assets = Array.isArray(summary.assets) ? summary.assets : []
  const counts = summary.counts || {}
  const filtered = useMemo(() => assets.filter(item => (
    (!keyword || [item.name, item.systemName, item.environment, item.ownerName, item.ownerDepartmentName].join(' ').toLowerCase().includes(keyword.toLowerCase())) &&
    (type === 'all' || item.assetType === type) &&
    (status === 'all' || item.healthStatus === status)
  )), [assets, keyword, type, status])

  const register = () => {
    form.validateFields().then(values => {
      setSaving(true)
      createRuntimeAsset({ ...values, timezone: 'Asia/Shanghai' })
        .then(value => {
          setRegisterVisible(false)
          form.resetFields()
          setRegistration(value)
          load()
        })
        .catch(() => message.error('运行资产登记失败，请检查填写内容或稍后重试'))
        .finally(() => setSaving(false))
    })
  }

  return <div>
    <Nav active="assets" />
    <div className="aic-page-header aic-runtime-header">
      <Typography.Title level={1}>运行资产</Typography.Title>
      <Typography.Paragraph type="secondary">统一登记和观察 Vibe 项目、微服务、采集器、周期任务及其他外部系统。</Typography.Paragraph>
    </div>
    {error && <Alert className="aic-runtime-alert" showIcon type="warning" message={error} action={<Button size="small" onClick={load}>重试</Button>} />}
    <div className="aic-runtime-stats">
      {[
        ['全部资产', counts.total || 0, 'total'], ['健康', counts.healthy || 0, 'healthy'], ['预警', counts.warning || 0, 'warning'],
        ['故障', counts.failure || 0, 'failure'], ['未知', counts.unknown || 0, 'unknown']
      ].map(([label, value, key]) => <Card key={key} className={`aic-runtime-stat aic-runtime-stat-${key}`}><Statistic title={label} value={value} /></Card>)}
    </div>
    <div className="aic-toolbar aic-runtime-toolbar">
      <Input allowClear prefix={<SearchOutlined />} placeholder="搜索资产、系统、环境或负责人" value={keyword} onChange={event => setKeyword(event.target.value)} />
      <Select value={type} onChange={setType} options={[{ value: 'all', label: '全部类型' }, ...assetTypeOptions]} />
      <Select value={status} onChange={setStatus} options={[{ value: 'all', label: '全部状态' }, ...Object.keys(statusMeta).map(value => ({ value, label: statusMeta[value].label }))]} />
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setRegisterVisible(true)}>登记运行资产</Button>
    </div>
    <Card className="aic-panel" title={`运行资产（${filtered.length}/${assets.length}）`}>
      <Table
        loading={loading}
        rowKey="id"
        dataSource={filtered}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        locale={{ emptyText: <Empty description="尚未登记运行资产" /> }}
        columns={[
          { title: '资产名称', dataIndex: 'name', render: display },
          { title: '类型', dataIndex: 'assetType', render: value => display(assetTypeLabels[value] || value) },
          { title: '所属系统', dataIndex: 'systemName', render: display },
          { title: '环境', dataIndex: 'environment', render: display },
          { title: '状态', dataIndex: 'healthStatus', render: value => { const meta = getStatusMeta(value); return <Badge status={meta.badge} text={meta.label} /> } },
          { title: '最近成功', dataIndex: 'lastSuccessAt', width: 180, render: value => formatDateTime(value, '-') },
          { title: '下次期望', dataIndex: 'nextExpectedAt', width: 180, render: value => formatDateTime(value, '-') },
          { title: '负责人', dataIndex: 'ownerName', render: display },
          { title: '操作', width: 70, render: (_, row) => <Button type="link" onClick={() => setDetailId(row.id)}>详情</Button> }
        ]}
      />
    </Card>

    <Modal visible={registerVisible} title="登记运行资产" confirmLoading={saving} okText="登记并生成上报密钥" cancelText="取消" onOk={register} onCancel={() => setRegisterVisible(false)} destroyOnClose>
      <Alert className="aic-runtime-form-note" showIcon type="info" message="登记后由运行端主动上报" description="上报密钥仅首次展示；状态由最近成功、失败事件和预期周期统一判定。" />
      <Form form={form} layout="vertical" initialValues={{ assetType: 'scheduled_task', environment: 'production', expectedIntervalMinutes: 60, gracePeriodMinutes: 0, visibilityScope: 'private' }}>
        <Form.Item name="name" label="资产名称" rules={[{ required: true, message: '请输入资产名称' }]}><Input maxLength={100} placeholder="例如：销售日报采集任务" /></Form.Item>
        <div className="aic-runtime-form-grid">
          <Form.Item name="assetType" label="资产类型" rules={[{ required: true }]}><Select options={assetTypeOptions} /></Form.Item>
          <Form.Item name="systemName" label="所属系统"><Input maxLength={100} placeholder="例如：lechun-tools" /></Form.Item>
          <Form.Item name="environment" label="环境" rules={[{ required: true }]}><Select options={['production', 'test', 'development'].map(value => ({ value, label: value }))} /></Form.Item>
          <Form.Item name="visibilityScope" label="可见范围" rules={[{ required: true }]}><Select options={Object.keys(visibilityLabels).map(value => ({ value, label: visibilityLabels[value] }))} /></Form.Item>
          <Form.Item name="expectedIntervalMinutes" label="预期运行周期（分钟）" rules={[{ required: true, message: '请输入预期运行周期' }]}><InputNumber min={1} max={525600} precision={0} /></Form.Item>
          <Form.Item name="gracePeriodMinutes" label="宽限时间（分钟）"><InputNumber min={0} max={525600} precision={0} /></Form.Item>
        </div>
        <Form.Item name="description" label="说明"><Input.TextArea rows={3} maxLength={500} placeholder="说明该资产负责什么、由谁运行及异常影响" /></Form.Item>
      </Form>
    </Modal>
    <CredentialModal registration={registration} onClose={() => setRegistration(null)} />
    <RuntimeAssetDetail assetId={detailId} visible={Boolean(detailId)} onClose={() => setDetailId(null)} />
  </div>
}
